<template>
  <v-container>
    <h1>Department Hours Manager</h1>
    <v-divider></v-divider>
    <p class="nav-tag">
      <span @click="goToMainDashboard">Main Dashboard</span> /
      {{ department.title }}
    </p>

    <v-divider></v-divider>

    <h2>{{ department.title }}</h2>

    <p class="description">
      Only authorized personnel are able to manage their departmental hours in
      the
      <a href="https://www.adelphi.edu/departments/" target="_blank">institutional directory</a>. If you have any
      questions, please contact the Digital Team at
      <a href="mailto:digital@adelphi.edu">digital@adelphi.edu</a>.
    </p>

    <div v-if="
      department.schedule_groups.length === 0 &&
      showNewHourGroup === false &&
      isEditing === false
    " class="no-hour-prompt-container">
      <div class="no-hour-prompt">
        <v-icon color="#2196F3">mdi-alert-circle-outline</v-icon>
        <div>
          <p class="no-hour-message1">No Saved Hours</p>
          <p class="no-hour-message2">
            There are currently no saved hours for this department. To begin,
            add a new hour group to display operational hours.
          </p>
        </div>
      </div>
    </div>

    <transition name="fade">
      <div v-if="showSaveMessage" class="no-hour-prompt save-successful-container">
        <v-icon color="green">mdi-check-circle-outline</v-icon>
        <div>
          <p class="no-hour-message1">Success: Hours Saved</p>
          <p class="no-hour-message2">
            You have successfully saved operational hours for this department.
          </p>
          <p class="no-hour-message2">
            Please allow 5-10 minutes for your changes to sync to the directory.
          </p>
        </div>
      </div>
    </transition>

    <div>
      <h3 :style="{ paddingBottom: '20px' }">Hours of Operation</h3>
      <div v-if="showNewHourGroup === false && isEditing === false">
        <div v-for="group in department.schedule_groups" :key="group.id">
          <div v-for="schedule in group.schedules" :key="schedule.id" :style="{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '20px',
          }">
            <div v-if="schedule.default_hours === false" :style="{
              marginLeft: '20px',
              marginRight: '10px',
            }"></div>
            <v-sheet elevation="3" class="my-custom-sheet" rounded>
              <div class="schedule-card-content">
                <div :style="{ display: 'flex', gap: '5px', alignItem: 'center' }">
                  <p class="form-title-text">
                    {{ schedule.form_title || schedule.title }}
                  </p>
                  <p class="schedule-card-description">
                    {{
                      schedule.default_hours
                        ? "— Default"
                        : "— Scheduled Revision"
                    }}
                  </p>
                </div>
                <div :style="{ display: 'flex', gap: '12px' }">
                  <StatusBar :statusText="getScheduleStatus(schedule, group)"/>

                  <v-menu offset-y v-model="DotActiveMenu[schedule.id]">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon v-bind="attrs" v-on="on" @click="dotToggleMenu(schedule.id)">
                        mdi-dots-vertical
                      </v-icon>
                    </template>
                    <v-list>
                      <v-list-item @click="dotEditSchedule(schedule, group)">
                        <v-list-item-title>Edit</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="dotDeleteSchedule(schedule, group)">
                        <v-list-item-title>Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>

                  <!-- <v-icon @click="handleDotPress">mdi-dots-vertical</v-icon> -->
                </div>
              </div>
            </v-sheet>
          </div>
        </div>

        <div class="add-hour-group-btn" @click="openNewForm">
          <v-icon color="#7A6855">mdi-plus-circle</v-icon>
          <p>ADD NEW HOURS GROUP</p>
        </div>
      </div>
    </div>

    <div v-if="showNewHourGroup || isEditing">
      <v-sheet elevation="3" class="my-custom-sheet" rounded>
        <v-form class="hour-group-form" @submit.prevent="submitForm">
          <div class="form-title">
            <!-- <p class="form-title-text">
              {{ isEditing ? "Edit Hours Group" : "Edit New Hours Group" }}
            </p> -->
            <v-text-field class="form-title-field" v-model="formTitle" :rules="titleRules" ref="hourTitle">
              <template v-slot:label>
                <span class="form-title-text">Form Title</span>
              </template>
            </v-text-field>

            <StatusBar :statusText="isEditing ? editingStatus.statusText : 'inactive'" />
          </div>
          <p class="hour-subtitle" :style="{ paddingBottom: '30px' }">
            Enter the title that will be diplayed in the hours management tool.
            If the title is empty, it will be the same as the label.
          </p>
          <v-divider></v-divider>
          <div v-if="allTemplateSchedules.length > 0">
            <p class="hour-title" :style="{ paddingTop: '30px' }">
              Choose a template (optional)
            </p>
            <p class="hour-subtitle" :style="{
              marginBottom: '10px',
            }">
              Duplicate an existing schedule to quickly create a similar
              schedule
            </p>
            <v-select label="Existing Templates" :items="allTemplateSchedules" :item-text="getItemText" item-value="id"
              v-model="selectedTemplateId" outlined class="override-dropdown"></v-select>
            <v-divider :style="{
              marginBottom: '30px',
            }"></v-divider>
          </div>

          <p class="hour-title">Displayed Label (optional)</p>
          <p class="hour-subtitle" :style="{ paddingBottom: '10px' }">
            Enter the label that will be displayed for this group of operational
            hours in the directory.
          </p>

          <v-text-field v-model="newHourLabel" class="hour-label-container" outlined :rules="titleRules"
            ref="newHourLabel">
            <template v-slot:label>
              <span class="custom-label">Name of Hours Label</span>
            </template>
          </v-text-field>
          <v-divider></v-divider>
          <div class="hour-section">
            <div>
              <p class="hour-title">Default Hours</p>
              <p class="hour-subtitle">
                Set these hours as the default operating hours when no custom
                hours are scheduled.
              </p>
            </div>
            <v-switch v-model="defaultHourSwitch" hide-details inset class="custom-switch" :class="{
              'switch-on': defaultHourSwitch,
              'switch-off': !defaultHourSwitch,
            }" @change="onDefaultHourSwitchChange" :rules="defaultSwitchRules" :disabled="isEditing"></v-switch>
          </div>
          <v-divider></v-divider>
          <div class="hour-section">
            <div>
              <p class="hour-title">Enable</p>
              <p class="hour-subtitle">
                Easily enable or disable operational hours. If you’re setting
                hours for a specific date range, make sure default hours are
                already saved with "Enable Switch Off". This ensures the hours
                for that date range are overridden correctly.
              </p>
            </div>
            <v-switch v-model="enableSwitch" hide-details inset class="custom-switch" :class="{
              'switch-on': enableSwitch,
              'switch-off': !enableSwitch,
            }" :disabled="isEditing && overrideHoursSwitch"></v-switch>
          </div>
          <div v-show="enableSwitch === true">
            <div v-for="(isEnabled, day) in weekdaySwitch" :key="day"
              :class="{ 'dimmed-section': allDaysCoverageSwitch }">
              <v-row class="hour-data-row" :ref="`${day}HourWrapper`">
                <v-col cols="7" md="auto" class="hour-data-col fixed-column" :style="{ paddingLeft: '20px' }">
                  <v-switch v-model="weekdaySwitch[day]" hide-details inset class="custom-switch" :class="{
                    'switch-on': isEnabled,
                    'switch-off': !isEnabled,
                  }"></v-switch>
                  <p class="weekday-text">{{ day }}</p>
                </v-col>
                <v-col cols="5" md="auto" class="hour-data-col" v-if="!isEnabled">
                  <img src="../assets/moon.svg" alt="Moon Icon" :style="{ paddingLeft: '20px' }" />
                  <p class="weekday-text" :style="{ color: 'rgba(18, 18, 18, 0.55)' }">
                    Closed
                  </p>
                </v-col>
                <v-col cols="12" sm="auto" md="auto" class="hour-data-col fixed-column" v-if="isEnabled"
                  :style="{ paddingLeft: '20px' }">
                  <div class="time_container">
                    <vue-timepicker v-model="weekdayHours[day].startHour" format="hh:mm" placeholder="Start time"
                      :use12Hour="true" :minute-interval="5" @input="hideStartHourErrorMessage(day)"
                      :disabled="weekdayHours[day].all_day_coverage" :class="{
                        'dimmed-section': weekdayHours[day].all_day_coverage,
                      }">
                    </vue-timepicker>
                    <v-icon color="rgba(119, 119, 118, 1)" class="time-icon">mdi-clock-time-four-outline</v-icon>
                    <div v-if="weekdayHours[day].startHourError" class="error-message">
                      Start Time is required.
                    </div>
                  </div>

                  <div class="start_time_am_pm">
                    <p :class="{
                      active: weekdayHours[day].startHourAmSelected,
                      'dimmed-section': weekdayHours[day].all_day_coverage,
                    }" @click="weekdayHours[day].startHourAmSelected = true"
                      :disabled="weekdayHours[day].all_day_coverage">
                      AM
                    </p>
                    <p :class="{
                      active: !weekdayHours[day].startHourAmSelected,
                      'dimmed-section': weekdayHours[day].all_day_coverage,
                    }" @click="weekdayHours[day].startHourAmSelected = false"
                      :disabled="weekdayHours[day].all_day_coverage">
                      PM
                    </p>
                  </div>
                </v-col>

                <v-col cols="12" sm="auto" md="auto" class="hour-data-col fixed-column" v-if="isEnabled"
                  :style="{ paddingLeft: '20px' }">
                  <div class="time_container">
                    <vue-timepicker v-model="weekdayHours[day].endHour" format="hh:mm" placeholder="End time"
                      :use12Hour="true" :minute-interval="5" @input="hideEndHourErrorMessage(day)"
                      :disabled="weekdayHours[day].all_day_coverage" :class="{
                        'dimmed-section': weekdayHours[day].all_day_coverage,
                      }">
                    </vue-timepicker>
                    <v-icon color="rgba(119, 119, 118, 1)" class="time-icon">mdi-clock-time-four-outline</v-icon>
                    <div v-show="weekdayHours[day].endHourError" class="error-message">
                      {{ weekdayHours[day].endHourMessage }}
                    </div>
                  </div>

                  <div class="start_time_am_pm">
                    <p :class="{
                      active: weekdayHours[day].endHourAmSelected,
                      'dimmed-section': weekdayHours[day].all_day_coverage,
                    }" @click="weekdayHours[day].endHourAmSelected = true"
                      :disabled="weekdayHours[day].all_day_coverage">
                      AM
                    </p>
                    <p :class="{
                      active: !weekdayHours[day].endHourAmSelected,
                      'dimmed-section': weekdayHours[day].all_day_coverage,
                    }" @click="weekdayHours[day].endHourAmSelected = false"
                      :disabled="weekdayHours[day].all_day_coverage">
                      PM
                    </p>
                  </div>
                </v-col>
                <v-col cols="12" sm="auto" md="auto" class="hour-data-col fixed-column" v-if="isEnabled"
                  :style="{ paddingLeft: '20px' }">
                  <v-switch v-model="weekdayHours[day].all_day_coverage" hide-details inset class="custom-switch"
                    :class="{
                      'switch-on': weekdayHours[day].all_day_coverage,
                      'switch-off': !weekdayHours[day].all_day_coverage,
                    }"></v-switch>
                  <p class="all_day_coverage_text">24 Hours</p>
                </v-col>
              </v-row>
            </div>

            <!-- <v-divider></v-divider>
            <div class="hour-section">
              <div>
                <p class="hour-title">24/7 Coverage</p>
                <p class="hour-subtitle">
                  Quickly specify if you are always open
                </p>
              </div>
              <v-switch
                v-model="allDaysCoverageSwitch"
                hide-details
                inset
                class="custom-switch"
                :class="{
                  'switch-on': allDaysCoverageSwitch,
                  'switch-off': !allDaysCoverageSwitch,
                }"
              ></v-switch>
            </div> -->
            <v-divider></v-divider>
            <p class="hour-title" :style="{ paddingTop: '30px' }">
              Custom Label (optional)
            </p>
            <p class="hour-subtitle" :style="{ paddingBottom: '5px' }">
              Enter the text that will be displayed under the hour schedule.
            </p>
            <v-text-field v-model="hoursCustomLabel" class="hour-label-container" outlined :rules="customLabelRules"
              ref="customLabel">
              <template v-slot:label>
                <span class="custom-label">Custom Label</span>
              </template>
            </v-text-field>
            <v-divider></v-divider>
            <div>
              <div class="hour-section" :style="{ paddingBottom: '20px' }">
                <div>
                  <p class="hour-title">Override Hours</p>
                  <p class="hour-subtitle">
                    Specify if these hours should override default hours during
                    the scheduled time
                  </p>
                </div>
                <v-switch v-model="overrideHoursSwitch" hide-details inset class="custom-switch" :class="{
                  'switch-on': overrideHoursSwitch,
                  'switch-off': !overrideHoursSwitch,
                }" @change="onOverrideHoursChange" :disabled="isEditing"></v-switch>
              </div>
              <div :style="{ margin: '20px 0' }" v-if="overrideHoursSwitch">
                <div :style="{ marginBottom: '30px' }">
                  <p class="hour-title">Schedule Hours</p>
                  <p class="hour-subtitle">
                    Set a date range to override default hours, which will
                    automatically publish and expire in the directory
                  </p>
                </div>
                <v-row class="hour-data-row">
                  <v-col cols="12" md="auto" class="hour-data-col fixed-column1"
                    :style="{ paddingLeft: '10px', paddingBottom: '10px' }">
                    <v-menu v-model="scheduleHours.startDateMenu" :close-on-content-click="false"
                      transition="scale-transition" offset-y max-width="290px" min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="scheduleHours.startDateFormatted" label="Start Date"
                          append-icon="mdi-calendar" v-bind="attrs" @blur="
                            date = parseDate(scheduleHours.startDateFormatted)
                            " v-on="on" outlined :rules="startDateRules" ref="startDateText"></v-text-field>
                      </template>
                      <v-date-picker v-model="scheduleHours.startDate" no-title
                        @input="scheduleHours.startDateMenu = false"></v-date-picker>
                    </v-menu>
                  </v-col>
                </v-row>

                <v-row class="hour-data-row">
                  <v-col cols="12" md="auto" class="hour-data-col fixed-column1"
                    :style="{ paddingLeft: '10px', position: 'relative' }">
                    <v-menu v-model="scheduleHours.endDateMenu" :close-on-content-click="false"
                      transition="scale-transition" offset-y max-width="290px" min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="scheduleHours.endDateFormatted" label="End Date"
                          append-icon="mdi-calendar" v-bind="attrs" @blur="
                            date = parseDate(scheduleHours.endDateFormatted)
                            " v-on="on" outlined :rules="endDateRules" ref="endDateText"></v-text-field>
                      </template>
                      <v-date-picker v-model="scheduleHours.endDate" no-title
                        @input="scheduleHours.endDateMenu = false"></v-date-picker> </v-menu></v-col>
                </v-row>
              </div>
              <v-select label="Specify Hours Group" :items="overrideHoursOptions" item-text="title" item-value="id"
                v-model="selectedGroupId" outlined class="override-dropdown" v-if="overrideHoursSwitch"
                :rules="selectedGroupIdRules" ref="selectHoursGroup" :disabled="isEditing"></v-select>
            </div>
          </div>

          <v-divider></v-divider>
          <div class="form-footer">
            <v-btn @click="discardChangesDialog = true">
              Discard Changes
            </v-btn>
            <v-btn color="#FFB500" :style="{ marginLeft: '20px' }" type="submit">
              Save Changes
            </v-btn>
          </div>
          <Dialog :dialog.sync="discardChangesDialog" @confirm="addHoursDiscardChanges" title="Discard Unsaved Changes?"
            message="You will lose all changes made to this operational hours group." btn2Text="discard changes"
            btn1Text="keep editing" />
          <Dialog :dialog.sync="saveDateConflictDialog" @confirm="onSaveConflictChanges" title="Save Conflict"
            message="There is a date conflict between these new changes and the active/scheduled  operational hours for this department. Please adjust these settings."
            btn2Text="BACK TO EDIT" />
          <Dialog :dialog.sync="saveSwitchConflictChanges" @confirm="onSaveSwitchConflictChanges" title="Save Conflict"
            message="Either the default hour switch or override hour switch must be on. Please adjust these settings."
            btn2Text="BACK TO EDIT" />

          <Dialog :dialog.sync="maxHourGroupDialog" @confirm="onMaxHourGroupComfirm" title="Max Hour Group Reached!"
            message="The maximum number of default hour is three, please delete one before creating more."
            btn2Text="BACK" />
        </v-form>
      </v-sheet>
    </div>

    <div>
      <h3>Closures</h3>
      <div v-if="showNewClosure === false">
        <v-sheet elevation="3" class="my-custom-sheet" rounded v-for="closure in department.closure" :key="closure.id"
          :style="{
            marginBottom: '20px',
          }">
          <div class="schedule-card-content">
            <p class="schedule-card-description">
              {{
                closure.startDate === closure.endDate
                  ? formatClosureDate(closure.startDate)
                  : `${formatClosureDate(
                    closure.startDate
                  )} to ${formatClosureDate(closure.endDate)}`
              }}
            </p>

            <v-menu offset-y v-model="DotActiveMenu[`closure ${closure.id}`]">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" @click="dotToggleMenu(`closure ${closure.id}`)">
                  mdi-dots-vertical
                </v-icon>
              </template>
              <v-list>
                <v-list-item @click="handleEditClosureDate(closure)">
                  <v-list-item-title>Edit</v-list-item-title>
                </v-list-item>
                <v-list-item @click="handleDeleteClosureDate(closure.id)">
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <!-- <v-icon @click="handleDeleteClosureDate(closure.id)"
              >mdi-close-circle</v-icon
            > -->
          </div>
        </v-sheet>
        <div @click="showNewClosure = true" class="add-hour-group-btn">
          <v-icon color="#7A6855">mdi-plus-circle</v-icon>
          <p>Schedule New Closure</p>
        </div>
      </div>
    </div>
    <div v-if="showNewClosure">
      <v-sheet max-width="420px" elevation="3" class="my-custom-sheet" rounded>
        <div :style="{
          padding: '16px 30px',
        }">
          <p class="form-title-text" v-if="!closureEditting">
            Add New Closure Date
          </p>
          <p class="form-title-text" v-if="closureEditting">
            Edit Closure Date
          </p>
          <p class="hour-subtitle">
            For creating the single closure date, select the same date for both
            the start and end date.
          </p>
        </div>
        <v-divider></v-divider>
        <div :style="{
          display: 'flex',
          justifyContent: 'center',
        }">
          <div class="closure-container">
            <v-menu v-model="newClosure.startClosureMenu" :close-on-content-click="false" transition="scale-transition"
              offset-y max-width="290px" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="newClosure.startDateFormatted" label="Start Date" append-icon="mdi-calendar"
                  v-bind="attrs" @blur="date = parseDate(newClosure.startDateFormatted)" v-on="on" outlined
                  :rules="[(v) => !!v || 'Date is required']" ref="newStartClosureDate"></v-text-field>
              </template>
              <v-date-picker v-model="newClosure.startDate" no-title
                @input="newClosure.startClosureMenu = false"></v-date-picker>
            </v-menu>

            <v-menu v-model="newClosure.endClosureMenu" :close-on-content-click="false" transition="scale-transition"
              offset-y max-width="290px" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field v-model="newClosure.endDateFormatted" label="End Date" append-icon="mdi-calendar"
                  v-bind="attrs" @blur="date = parseDate(newClosure.endDateFormatted)" v-on="on" outlined :rules="[
                    (v) => !!v || 'Date is required',
                    (v) =>
                      !newClosure.startDate ||
                      new Date(v) >= new Date(newClosure.startDate) ||
                      'End Date cannot be earlier than Start Date',
                  ]" ref="newEndClosureDate"></v-text-field>
              </template>
              <v-date-picker v-model="newClosure.endDate" no-title
                @input="newClosure.endClosureMenu = false"></v-date-picker>
            </v-menu>
          </div>
        </div>
        <v-divider></v-divider>
        <div :style="{
          display: 'flex',
          justifyContent: 'center',
          alignItem: 'center',
          padding: '16px',
        }">
          <v-btn @click="
            showNewClosure = false;
          resetClosureDates();
          ">
            Cancel
          </v-btn>
          <v-btn v-if="!closureEditting" color="#FFB500" :style="{ marginLeft: '20px' }" @click="handleAddNewClosure">
            Add
          </v-btn>
          <v-btn v-if="closureEditting" color="#FFB500" :style="{ marginLeft: '20px' }"
            @click="onSubmitEditClosureDate">
            Save
          </v-btn>
        </div>
      </v-sheet>
    </div>

    <div :style="{
      marginTop: '20px',
      marginBottom: '30px',
    }">
      <h3>Additional Hours Link</h3>
      <div v-if="showNewAdditionalHour === false">
        <v-sheet elevation="3" class="my-custom-sheet" rounded v-if="department.additional_hours_url" :style="{
          marginBottom: '20px',
        }">
          <div class="schedule-card-content">
            <p class="form-title-text">{{ department.additional_hours_url }}</p>
            <!-- <v-icon @click="handleDeleteAdditionalHourLink"
              >mdi-close-circle</v-icon
            > -->
            <v-menu offset-y v-model="DotActiveMenu[`additionalHourLink`]">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" @click="dotToggleMenu(`additionalHourLink`)">
                  mdi-dots-vertical
                </v-icon>
              </template>
              <v-list>
                <v-list-item @click="
                  handleEditAdditionalHour(department.additional_hours_url)
                  ">
                  <v-list-item-title>Edit</v-list-item-title>
                </v-list-item>
                <v-list-item @click="handleDeleteAdditionalHourLink">
                  <v-list-item-title>Delete</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </v-sheet>
        <div @click="showNewAdditionalHour = true" class="add-hour-group-btn" v-if="!department.additional_hours_url">
          <v-icon color="#7A6855">mdi-plus-circle</v-icon>
          <p>Add An Hour Link</p>
        </div>
      </div>

      <div v-if="showNewAdditionalHour === true">
        <div :style="{
          margin: '10px 20px',
          display: 'flex',
          alignItems: 'flex-end',
          gap: '20px',
        }">
          <v-text-field v-model="additionalHourLink" :style="{
            height: '30px',
            maxWidth: '400px',
            padding: '0',
            margin: '0',
          }" :rules="additionalHourLinkRules" ref="additionalHourLinkField">
            <template v-slot:label>
              <span :style="{ color: '#858585', fontWeight: '300' }">New Hour Link</span>
            </template>
          </v-text-field>
          <v-btn @click="
            showNewAdditionalHour = false;
          resetAdditionalHour();
          ">
            cancel
          </v-btn>
          <v-btn @click="handleAddNewHourLink" color="#FFB500" v-if="!additionalHourEditting">
            Add
          </v-btn>
          <v-btn @click="handleSaveAdditionalHourLink" color="#FFB500" v-if="additionalHourEditting">
            Save
          </v-btn>
        </div>
      </div>
    </div>
    <Dialog :dialog.sync="deleteConflictDialog" @confirm="onDeleteConflictChanges" title="Delete Conflict"
      message="Please delete override hours before deleting the default hour!" btn2Text="BACK" />
    <Dialog :dialog.sync="deleteConfirmationDialog" @confirm="onDeleteConfirmationChanges" title="Delete Hours?"
      message="You will loose all the information entered for this operational hours." btn2Text="DELETE HOURS"
      btn1Text="KEEP IT" />
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="4000" top>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import VueTimepicker from "vue2-timepicker";
import "vue2-timepicker/dist/VueTimepicker.css";
import StatusBar from "./hourManager/StatusBar.vue";
import Dialog from "./hourManager/Dialog.vue";
import DOMPurify from "dompurify";
import apiClient from "../services/apiClient";

export default {
  components: {
    VueTimepicker,
    StatusBar,
    Dialog,
  },
  name: "DepartmentSchedules",
  props: {
    department: {
      type: Object,
      required: true,
    },
  },

  data() {
    // const initialDate = new Date(
    //   Date.now() - new Date().getTimezoneOffset() * 60000
    // );
    return {
      apiUrl: process.env.VUE_APP_API_BASE_URL,
      allTemplateSchedules: [],
      selectedTemplateId: null,
      showNewHourGroup: false,
      isEditing: false,
      selectedEditGroupId: null,
      selectedEditScheduleId: null,
      editingStatus: {},
      showNewClosure: false,
      showNewAdditionalHour: false,
      formTitle: "",
      newHourLabel: "",
      defaultHourSwitch: true,
      enableSwitch: false,
      allDaysCoverageSwitch: false,
      overrideHoursSwitch: false,
      additionalHourLink: "",
      discardChangesDialog: false,
      saveDateConflictDialog: false,
      deleteConflictDialog: false,
      deleteConfirmationDialog: false,
      maxHourGroupDialog: false,
      saveSwitchConflictChanges: false,
      closureEditting: false,
      selectedEditClosureId: null,
      additionalHourEditting: false,
      hoursCustomLabel: "",
      snackbar: {
        show: false,
        message: '',
        color: 'error'
      },
      weekdaySwitch: {
        Sunday: false,
        Monday: false,
        Tuesday: false,
        Wednesday: false,
        Thursday: false,
        Friday: false,
        Saturday: false,
      },
      weekdayHours: {
        Sunday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
        Monday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
        Tuesday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
        Wednesday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
        Thursday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
        Friday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
        Saturday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
      },
      scheduleHours: {
        startDateMenu: false,
        endDateMenu: false,
        startDate: null,
        // endDate: initialDate.toISOString(),
        endDate: null,
        startDateFormatted: "",
        endDateFormatted: "",
      },

      overrideHoursOptions: [],
      selectedGroupId: null,
      weekdayMapping: {
        Sunday: 7,
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6,
      },
      reverseWeekdayMapping: {
        7: "Sunday",
        1: "Monday",
        2: "Tuesday",
        3: "Wednesday",
        4: "Thursday",
        5: "Friday",
        6: "Saturday",
      },

      newClosure: {
        startClosureMenu: false,
        endClosureMenu: false,
        startDate: null,
        endDate: null,
        startDateFormatted: "",
        endDateFormatted: "",
      },
      showSaveMessage: false,
      DotActiveMenu: {},
      scheduleDeleteUrl: "",
      titleRules: [
        (v) => !v || v.length < 46 || "Title must be less than 46 characters",
      ],
      customLabelRules: [
        (v) => !v || v.length < 80 || "Title must be less than 80 characters",
      ],
      defaultSwitchRules: [
        () =>
          !!this.overrideHoursSwitch ||
          !!this.defaultHourSwitch ||
          "Either 'Default Hours' or 'Override Hours' must be enabled.",
      ],
      startDateRules: [
        (v) =>
          !this.overrideHoursSwitch ||
          !!v ||
          "Start date is required when Override Hours is on.",
      ],
      endDateRules: [
        (v) =>
          !this.overrideHoursSwitch ||
          !!v ||
          "End date is required when Override Hours is on.",
        (v) =>
          !this.overrideHoursSwitch ||
          (
            this.scheduleHours.startDateFormatted &&
            new Date(this.parseDate(v)) >=
            new Date(this.parseDate(this.scheduleHours.startDateFormatted))
          ) ||
          "End date cannot be earlier than the start date.",
      ],
      //rule will only trigger when the selected group dropdown is rendered
      selectedGroupIdRules: [(v) => !!v || "You must select an hours group."],
      additionalHourLinkRules: [
        (v) => !!v || "Link is required",
        (v) => this.isValidUrl(v) || "Must be a valid URL",
      ],
    };
  },
  created() { },
  // updated() {
  //   window.console.log("Component updated");
  // },
  computed: {},
  watch: {
    department: {
      handler(newDate) {
        this.allTemplateSchedules = newDate.schedule_groups.flatMap((group) =>
          group.schedules.map((schedule) => schedule)
        );
      },
      immediate: true, // Trigger the handler immediately on component creation
    },
    "scheduleHours.startDate"(newDate) {
      this.scheduleHours.startDateFormatted = this.formatDate(newDate);
    },
    "scheduleHours.endDate"(newDate) {
      this.scheduleHours.endDateFormatted = this.formatDate(newDate);
    },
    "newClosure.startDate"(newDate) {
      this.newClosure.startDateFormatted = this.formatDate(newDate);
    },
    "newClosure.endDate"(newDate) {
      this.newClosure.endDateFormatted = this.formatDate(newDate);
    },
    showNewHourGroup(newValue) {
      if (newValue) {
        this.populateOverrideHoursOptions();
      }
    },
    isEditing(newValue) {
      if (newValue) {
        this.populateOverrideHoursOptions();
      }
    },
    selectedTemplateId(newValue) {
      if (newValue) {
        const selectedSchedule = this.allTemplateSchedules.find(
          (templateSchedule) => templateSchedule.id === newValue
        );

        this.resetHoursField();
        this.populatedFormInitialValue(selectedSchedule);
      }
    },
 
  },
  methods: {
    showError(message) {
      this.snackbar = {
        show: true,
        message: message,
        color: 'error'
      };
    },
    getItemText(item) {
      return item.form_title || item.title;
    },
    isValidUrl(url) {
      const pattern = new RegExp(
        "^(https?:\\/\\/)?" + // protocol
        "((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.?)+[a-zA-Z]{2,}|\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}|\\[?[A-Fa-f\\d:]+\\]?)" + // domain name or IP
        "(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+]*)*" + // port and path
        "(\\?[;&a-zA-Z\\d%_.~+=-]*)?" + // query string
        "(\\#[-a-zA-Z\\d_]*)?$",
        "i"
      );
      return !!pattern.test(url);
    },
    sanitizeInput(input) {
      return DOMPurify.sanitize(input);
    },
    openNewForm() {
      this.showNewHourGroup = true;
      this.$nextTick(() => {
        this.$refs.hourTitle.focus();
      });
    },
    onDefaultHourSwitchChange() {
      if (this.defaultHourSwitch && this.overrideHoursSwitch) {
        this.overrideHoursSwitch = false;
      }
    },
    onOverrideHoursChange() {
      if (this.defaultHourSwitch && this.overrideHoursSwitch) {
        this.defaultHourSwitch = false;
      }
    },

    goToMainDashboard() {
      this.$router.push("/office_hour_manager");
    },

    selectAmPm(day, period) {
      const time = this.timeData.find((t) => t.day === day);
      if (time) {
        time.isAM = period === "AM";
      }
    },
    addHoursDiscardChanges() {
      this.discardChangesDialog = false;
      this.showNewHourGroup = false;
      this.isEditing = false;
      this.resetForm();
    },
    onSaveConflictChanges() {
      this.saveDateConflictDialog = false;
    },
    onSaveSwitchConflictChanges() {
      this.saveSwitchConflictChanges = false;
    },
    onDeleteConflictChanges() {
      this.deleteConflictDialog = false;
    },
    async onDeleteConfirmationChanges() {
      this.deleteConfirmationDialog = false;

      try {
        const response = await apiClient.delete(this.scheduleDeleteUrl);

        if (response.status === 204) {
          await this.updateDepartmentScheduleGroups();
        }
      } catch (error) {
        window.console.error("Error deleting the department:", error);
        this.showError('Failed to delete hours. Please try again.');
        // Handle the error, e.g., show a notification to the user
      }
    },

    addHoursSaveChanges() {
      this.showNewHourGroup = false;
    },

    onMaxHourGroupComfirm() {
      this.maxHourGroupDialog = false;
    },
    formatDate(date) {
      if (!date) return null;

      const [year, month, day] = date.split("-");
      return `${month}/${day}/${year}`;
    },
    parseDate(date) {
      if (!date) return null;

      const [month, day, year] = date.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    },
    isValidTime(time) {
      if (!time || typeof time !== "string") {
        return false;
      }
      const timePattern = /^(0?[1-9]|1[0-2]):[0-5][0-9]$/; // 12-hour format without AM/PM
      return timePattern.test(time);
    },
    parseTime(time) {
      const [hours, minutes] = time.split(":").map(Number);
      return { hours, minutes };
    },
    convertTo24HourFormat(time, isAmSelected) {
      let { hours, minutes } = this.parseTime(time);
      if (isAmSelected && hours === 12) {
        hours = 0; // 12 AM
      } else if (!isAmSelected && hours !== 12) {
        hours += 12; // PM case, except for 12 PM which remains 12
      }
      return { hours, minutes };
    },
    convertTo12HourFormat(time) {
      let [hour, minute] = time.split(":").map(Number);
      let isAmSelected = true; // AM by default

      if (hour >= 12) {
        isAmSelected = false; // PM
        if (hour > 12) {
          hour -= 12; // Convert to 12-hour format
        }
      } else if (hour === 0) {
        hour = 12; // Midnight edge case
      }

      const formattedHour = `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;

      return { hour: formattedHour, isAmSelected };
    },
    async updateDepartmentScheduleGroups() {
      try {
        const response = await apiClient.get(
          `${this.apiUrl}/v1/departments/${this.department.id}/`
        );

        // Update the department prop with the new schedule_groups data
        this.$set(this, "department", response.data);
        this.$router.replace({
          path: `/office_hour_manager/${this.department.id}`,
          query: {
            department: encodeURIComponent(JSON.stringify(response.data)),
          },
        });
      } catch (error) {
        window.console.error("Error updating department data:", error);
        this.showError('Failed to refresh department data. Please reload the page.');
      }
    },
    async submitForm() {
      let isFormValid = true;
      // if (!this.$refs.newHourLabel.validate()) {
      //   isFormValid = false;
      // }

      if (this.$refs.startDateText && !this.$refs.startDateText.validate()) {
        isFormValid = false;
      }
      if (this.$refs.endDateText && !this.$refs.endDateText.validate()) {
        isFormValid = false;
      }

      if (
        this.$refs.selectHoursGroup &&
        !this.$refs.selectHoursGroup.validate()
      ) {
        isFormValid = false;
      }

      const validHours = {};

      for (let day in this.weekdaySwitch) {
        if (
          this.weekdaySwitch[day] &&
          !this.weekdayHours[day].all_day_coverage
        ) {
          const { startHour, endHour, startHourAmSelected, endHourAmSelected } =
            this.weekdayHours[day];
          const startHourValid = this.isValidTime(startHour);
          const endHourValid = this.isValidTime(endHour);

          this.$set(this.weekdayHours[day], "startHourError", !startHourValid);
          this.$set(this.weekdayHours[day], "endHourError", !endHourValid);

          if (!startHourValid || !endHourValid) {
            isFormValid = false;
            this.$set(
              this.weekdayHours[day],
              "endHourMessage",
              "End Time is required"
            );
          } else {
            let startTime = this.convertTo24HourFormat(
              startHour,
              startHourAmSelected
            );
            let endTime = this.convertTo24HourFormat(
              endHour,
              endHourAmSelected
            );
            validHours[day] = {
              startHour: `${startTime.hours
                .toString()
                .padStart(2, "0")}:${startTime.minutes
                  .toString()
                  .padStart(2, "0")}:00`,
              endHour: `${endTime.hours
                .toString()
                .padStart(2, "0")}:${endTime.minutes
                  .toString()
                  .padStart(2, "0")}:00`,
              all_day_coverage: false,
            };
            // }
          }
        } else if (
          this.weekdaySwitch[day] &&
          this.weekdayHours[day].all_day_coverage
        ) {
          validHours[day] = {
            startHour: "00:00:00",
            endHour: "00:00:00",
            all_day_coverage: true,
          };
        }
      }
      if (
        this.department.schedule_groups.length >= 5 &&
        this.isEditing === false &&
        this.defaultHourSwitch
      ) {
        this.maxHourGroupDialog = true;
        return;
      }

      if (this.selectedGroupId && this.overrideHoursSwitch) {
        const inputStartDate = new Date(this.scheduleHours.startDate);
        const inputEndDate = new Date(this.scheduleHours.endDate);
        const scheduleGroup = this.department.schedule_groups.find(
          (group) => group.id === this.selectedGroupId
        );
        for (let schedule of scheduleGroup.schedules) {
          if (
            !schedule.default_hours &&
            !(this.isEditing && this.selectedEditScheduleId === schedule.id)
          ) {
            const scheduleStartDate = new Date(schedule.start_date);
            const scheduleEndDate = new Date(schedule.end_date);
            const isOverlapping =
              inputStartDate <= scheduleEndDate &&
              inputEndDate >= scheduleStartDate;

            if (isOverlapping) {
              this.saveDateConflictDialog = true;
              return;
            }
          }
        }
      }

      if (isFormValid) {
        if (this.formTitle === null || this.formTitle.trim() === "") {
          this.formTitle = null;
        } else {
          this.formTitle = this.sanitizeInput(this.formTitle.trim());
        }

        const titleLabel =
          this.sanitizeInput(this.newHourLabel) === ""
            ? "No Title"
            : this.sanitizeInput(this.newHourLabel);

        this.hoursCustomLabel = this.sanitizeInput(this.hoursCustomLabel);
        // Proceed with form submission or further processing

        try {
          if (!this.defaultHourSwitch && !this.overrideHoursSwitch) {
            this.saveSwitchConflictChanges = true;
            return;
          }

          let response;

          if (this.defaultHourSwitch) {
            const newPost = {
              form_title: this.formTitle,
              title: titleLabel,
              default_hours: true,
              weekdays: [],
              enable: false,
              custom_label: this.hoursCustomLabel,
            };

            if (this.enableSwitch) {
              newPost.enable = true;
              if (this.allDaysCoverageSwitch) {
                newPost.all_day_coverage = true;
              } else {
                for (const day in validHours) {
                  if (validHours.hasOwnProperty(day)) {
                    const hours = validHours[day];
                    const weekdayData = {
                      coverage_24hours: hours.all_day_coverage,
                      weekday: this.weekdayMapping[day],
                      open_time: hours.startHour,
                      close_time: hours.endHour,
                    };
                    newPost.weekdays.push(weekdayData);
                  }
                }
              }
            }

            if (this.isEditing) {
              response = await apiClient.put(
                `${this.apiUrl}/v1/departments/${this.department.id}/schedule_groups/${this.selectedEditGroupId}/schedules/${this.selectedEditScheduleId}/`,
                newPost

              );
            } else {
              const response1 = await apiClient.post(
                `${this.apiUrl}/v1/departments/${this.department.id}/schedule_groups/`,
                {}

              );
              if (response1?.data?.id) {
                response = await apiClient.post(
                  `${this.apiUrl}/v1/departments/${this.department.id}/schedule_groups/${response1.data.id}/schedules/`,
                  newPost

                );
              }
            }
          } else if (this.overrideHoursSwitch) {
            const newPost = {
              form_title: this.formTitle,
              title: titleLabel,
              default_hours: false,
              weekdays: [],
              start_date: this.scheduleHours.startDate,
              end_date: this.scheduleHours.endDate,
              enable: this.enableSwitch,
              custom_label: this.hoursCustomLabel,
            };

            if (this.allDaysCoverageSwitch) {
              newPost.all_day_coverage = true;
            } else {
              for (const day in validHours) {
                if (validHours.hasOwnProperty(day)) {
                  const hours = validHours[day];
                  const weekdayData = {
                    coverage_24hours: hours.all_day_coverage,
                    weekday: this.weekdayMapping[day],
                    open_time: hours.startHour,
                    close_time: hours.endHour,
                  };
                  newPost.weekdays.push(weekdayData);
                }
              }
            }

            if (this.isEditing) {
              response = await apiClient.put(
                `${this.apiUrl}/v1/departments/${this.department.id}/schedule_groups/${this.selectedEditGroupId}/schedules/${this.selectedEditScheduleId}/`,
                newPost

              );
            } else {
              response = await apiClient.post(
                `${this.apiUrl}/v1/departments/${this.department.id}/schedule_groups/${this.selectedGroupId}/schedules/`,
                newPost

              );
            }
          }
          if (response?.data) {
            await this.updateDepartmentScheduleGroups();
          }

        } catch (error) {
          window.console.error("post new data Error:", error);
          if (error.response) {
            const status = error.response.status;
            const message = error.response.data?.detail || error.response.data?.message;

            if (status === 400) {
              this.showError(message || 'Invalid data. Please check your inputs.');
            } else if (status === 403) {
              this.showError('You do not have permission to save these hours.');
            } else {
              this.showError(message || 'Failed to save hours. Please try again.');
            }
          } else {
            this.showError('Network error. Please check your connection.');
          }
          return;
        }

        this.showNewHourGroup = false;
        this.isEditing = false;
        this.resetForm();
        this.showSaveMessage = true;
        // Hide the message after 5 seconds
        setTimeout(() => {
          this.showSaveMessage = false;
        }, 12000);
      } else {
        this.focusFirstInvalidField();
      }
    },
    focusFirstInvalidField() {
      this.$nextTick(() => {
        for (let ref in this.$refs) {
          const element = this.$refs[ref];

          // Custom check for your timepickers
          if (ref.includes("HourWrapper")) {
            const day = ref.replace("HourWrapper", "");

            // Only check validation if the switch for this day is on
            if (this.weekdaySwitch[day]) {
              const startTime = this.weekdayHours[day].startHour;
              const endTime = this.weekdayHours[day].endHour;

              if (!this.isValidTime(startTime) || !this.isValidTime(endTime)) {
                document
                  .querySelectorAll(".time_container")
                  .forEach((element) => {
                    element.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                  });
                break;
              }
            }
          } else if (
            element &&
            typeof element.validate === "function" &&
            !element.validate(true)
          ) {
            const domElement = element.$el || element;
            if (domElement.scrollIntoView) {
              domElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }

            if (typeof domElement.focus === "function") {
              domElement.focus();
            }
            break; // Stop after the first invalid field is handled
          }
        }
      });
    },

    hideStartHourErrorMessage(day) {
      this.$set(this.weekdayHours[day], "startHourError", false);
    },
    hideEndHourErrorMessage(day) {
      this.$set(this.weekdayHours[day], "endHourError", false);
    },
    populateOverrideHoursOptions() {
      this.overrideHoursOptions = [];
      this.department.schedule_groups.forEach((schedulesGroup) => {
        const schedule = schedulesGroup.schedules.find(
          (schedule) => schedule.default_hours === true
        );

        if (schedule) {
          this.overrideHoursOptions.push({
            title: schedule.form_title || schedule.title || "Default Title",
            id: schedulesGroup.id,
          });
        }
      });
    },
    resetHoursField() {
      this.weekdaySwitch = {
        Sunday: false,
        Monday: false,
        Tuesday: false,
        Wednesday: false,
        Thursday: false,
        Friday: false,
        Saturday: false,
      };
      this.weekdayHours = {
        Sunday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
        Monday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
        Tuesday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
        Wednesday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
        Thursday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
        Friday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
        Saturday: {
          startHour: "",
          startHourAmSelected: true,
          endHourAmSelected: false,
          endHour: "",
          startHourError: false,
          endHourError: false,
          endHourMessage: "",
          all_day_coverage: false,
        },
      };
    },
    resetForm() {
      this.selectedTemplateId = null;
      this.newHourLabel = "";
      (this.formTitle = ""), (this.defaultHourSwitch = true);
      this.enableSwitch = false;
      this.allDaysCoverageSwitch = false;
      this.overrideHoursSwitch = false;
      this.hoursCustomLabel = "";
      this.resetHoursField();
      // const initialDate = new Date(
      //   Date.now() - new Date().getTimezoneOffset() * 60000
      // );
      this.scheduleHours = {
        startDateMenu: false,
        endDateMenu: false,
        startDate: null,
        endDate: null,
        startDateFormatted: "",
        endDateFormatted: "",
      };
      this.overrideHoursOptions = [];
      this.selectedGroupId = null;
    },
    compareScheduleDate(start_date, end_date) {
      const estTimeZone = "America/New_York";
      const currentDateString = new Date().toLocaleString("en-US", {
        timeZone: estTimeZone,
      });

      const currentDate = new Date(currentDateString);
      const startDate = new Date(`${start_date}T00:00:00`);
      const endDate = new Date(`${end_date}T23:59:59`);
      if (endDate < currentDate) {
        return "expired";
      } else if (startDate > currentDate) {
        return "upcoming";
      } else if (currentDate >= startDate && currentDate <= endDate) {
        return "active";
      }
    },
    getScheduleStatus(schedule, group) {
      if (!schedule.enable) {
        return "inactive";
      } else if (!schedule.default_hours) {
        return this.compareScheduleDate(schedule.start_date, schedule.end_date);
      } else if (schedule.default_hours) {
        for (let i = 0; i < group.schedules.length; i++) {
          if (
            !group.schedules[i].default_hours &&
            this.compareScheduleDate(
              group.schedules[i].start_date,
              group.schedules[i].end_date
            ) === "active"
          ) {
            return "inactive";
          }
        }
        return "active";
      }
    },
    dotToggleMenu(key) {
      if (!this.DotActiveMenu.hasOwnProperty(key)) {
        this.$set(this.DotActiveMenu, key, false);
      }
      this.DotActiveMenu[key] = !this.DotActiveMenu[key];
    },
    dotEditSchedule(schedule, group) {
      // Handle edit logic

      this.selectedEditGroupId = group.id;
      this.selectedEditScheduleId = schedule.id;
      this.populatedFormInitialValue(schedule);
      this.editingStatus = {
        statusText: this.getScheduleStatus(schedule, group),
      };
      this.isEditing = true;
      //close the dot menu
      this.$set(this.DotActiveMenu, schedule.id, false);
    },
    dotDeleteSchedule(schedule, group) {
      let url;
      if (schedule.default_hours && group.schedules.length > 1) {
        this.deleteConflictDialog = true;
        return;
      } else if (schedule.default_hours) {
        url = `${this.apiUrl}/v1/departments/${this.department.id}/schedule_groups/${group.id}/`;
      } else if (!schedule.default_hours) {
        url = `${this.apiUrl}/v1/departments/${this.department.id}/schedule_groups/${group.id}/schedules/${schedule.id}/`;
      }

      this.scheduleDeleteUrl = url;

      this.deleteConfirmationDialog = true;
    },
    populatedFormInitialValue(schedule) {
      if (this.selectedTemplateId === null) {
        this.newHourLabel = schedule.title === "No Title" ? "" : schedule.title;
        this.formTitle = schedule.form_title;
        this.defaultHourSwitch = schedule.default_hours;
        this.overrideHoursSwitch = !schedule.default_hours;
        if (schedule.start_date && schedule.end_date) {
          this.scheduleHours.startDate = schedule.start_date;
          this.scheduleHours.endDate = schedule.end_date;
          this.selectedGroupId = this.selectedEditGroupId;
        }

        this.allDaysCoverageSwitch = schedule.all_day_coverage;

        this.hoursCustomLabel = schedule.custom_label;
      }
      this.enableSwitch = schedule.enable;
      schedule.weekdays.forEach((weekday) => {
        this.weekdaySwitch[this.reverseWeekdayMapping[weekday.weekday]] = true;
        const { hour: startHour, isAmSelected: startHourAmSelected } =
          this.convertTo12HourFormat(weekday.open_time);
        const { hour: endHour, isAmSelected: endHourAmSelected } =
          this.convertTo12HourFormat(weekday.close_time);
        this.weekdayHours[this.reverseWeekdayMapping[weekday.weekday]] = {
          all_day_coverage: weekday.coverage_24hours,
          startHour,
          startHourAmSelected,
          endHour,
          endHourAmSelected,
        };
      });
    },

    async handleAddNewClosure() {
      const isValid = this.$refs.newStartClosureDate.validate();
      const isValid2 = this.$refs.newEndClosureDate.validate();
      if (isValid && isValid2) {

        const { startDate, endDate } = this.newClosure;
        try {
          const newPost = {
            startDate,
            endDate,
          };
          const response = await apiClient.post(
            `${this.apiUrl}/v1/departments/${this.department.id}/closure/`,
            newPost

          );
          if (response.data) {
            const newClosure = [...this.department.closure, response.data];
            this.$set(this.department, "closure", newClosure);
            this.$router.replace({
              path: `/office_hour_manager/${this.department.id}`,
              query: {
                department: encodeURIComponent(
                  JSON.stringify({
                    ...this.department,
                    closure: newClosure,
                  })
                ),
              },
            });
          }
        } catch (error) {
          window.console.error("Error deleting the closure:", error);
        }
        this.resetClosureDates();
        this.showNewClosure = false;
        // Reset newClosure object or any other necessary cleanup
      } else {
        this.focusFirstInvalidField();
        this.showError('Failed to add closure. Please try again.');
        return;
      }
    },
    handleEditClosureDate(closure) {
      this.newClosure.startDate = closure.startDate;
      this.newClosure.endDate = closure.endDate;
      this.selectedEditClosureId = closure.id;
      this.closureEditting = true;
      this.showNewClosure = true;
      this.$set(this.DotActiveMenu, `closure ${closure.id}`, false);
    },
    resetClosureDates() {
      this.newClosure = {
        startClosureMenu: false,
        endClosureMenu: false,
        startDate: null,
        endDate: null,
        startDateFormatted: "",
        endDateFormatted: "",
      };
      this.closureEditting = false;
    },
    async onSubmitEditClosureDate() {
      const isValid = this.$refs.newStartClosureDate.validate();
      const isValid2 = this.$refs.newEndClosureDate.validate();
      if (isValid && isValid2) {

        const { startDate, endDate } = this.newClosure;
        try {
          const newPost = {
            startDate,
            endDate,
          };
          const response = await apiClient.put(
            `${this.apiUrl}/v1/departments/${this.department.id}/closure/${this.selectedEditClosureId}/`,
            newPost

          );

          if (response.data) {
            const newClosure = this.department.closure.map((clo) => {
              if (clo.id === response.data.id) {
                return response.data;
              }
              return clo;
            });

            this.$set(this.department, "closure", newClosure);
            this.$router.replace({
              path: `/office_hour_manager/${this.department.id}`,
              query: {
                department: encodeURIComponent(
                  JSON.stringify({
                    ...this.department,
                    closure: newClosure,
                  })
                ),
              },
            });
          }
        } catch (error) {
          window.console.error("Error deleting the closure:", error);
          this.showError('Failed to update closure. Please try again.');
          return;
        }

        this.resetClosureDates();
        this.showNewClosure = false;
      } else {
        this.focusFirstInvalidField();
      }
    },
    async handleDeleteClosureDate(closureId) {

      try {
        const response = await apiClient.delete(
          `${this.apiUrl}/v1/departments/${this.department.id}/closure/${closureId}/`
        );

        if (response.status === 204) {
          const newClosure = this.department.closure.filter(
            (closure) => closure.id !== closureId
          );
          this.$set(this.department, "closure", newClosure);
          this.$router.replace({
            path: `/office_hour_manager/${this.department.id}`,
            query: {
              department: encodeURIComponent(
                JSON.stringify({
                  ...this.department,
                  closure: newClosure,
                })
              ),
            },
          });
        }
      } catch (error) {
        window.console.error("Error deleting the closure:", error);
        this.showError('Failed to delete closure. Please try again.');
      }
    },

    // additionalHourEditting
    handleEditAdditionalHour(link) {
      this.additionalHourLink = link;
      this.additionalHourEditting = true;
      this.showNewAdditionalHour = true;

      this.$set(this.DotActiveMenu, `additionalHourLink`, false);
    },
    resetAdditionalHour() {
      this.additionalHourLink = "";
      this.additionalHourEditting = false;
    },

    async handleSaveAdditionalHourLink() {
      if (!this.$refs.additionalHourLinkField.validate()) {
        return;
      }

      const newPost = { additional_hours_url: this.additionalHourLink };
      try {
        const response = await apiClient.patch(
          `${this.apiUrl}/v1/departments/${this.department.id}/`,
          newPost
        );

        if (response.data) {
          this.$set(
            this.department,
            "additional_hours_url",
            this.additionalHourLink
          );
          this.$router.replace({
            path: `/office_hour_manager/${this.department.id}`,
            query: {
              department: encodeURIComponent(
                JSON.stringify({
                  ...this.department,
                  additional_hours_url: this.additionalHourLink,
                })
              ),
            },
          });
        }
      } catch (error) {
        window.console.error("Error updating the additional hour:", error);
        this.showError('Failed to save link. Please try again.');
        return;
      }
      this.showNewAdditionalHour = false;
      this.resetAdditionalHour();
    },
    async handleDeleteAdditionalHourLink() {

      const newPost = { additional_hours_url: null };
      try {
        const response = await apiClient.patch(
          `${this.apiUrl}/v1/departments/${this.department.id}/`,
          newPost,

        );

        if (response.data) {
          this.$set(this.department, "additional_hours_url", null);
          this.$router.replace({
            path: `/office_hour_manager/${this.department.id}`,
            query: {
              department: encodeURIComponent(
                JSON.stringify({
                  ...this.department,
                  additional_hours_url: null,
                })
              ),
            },
          });
        }
      } catch (error) {
        window.console.error("Error deleting the additional hour:", error);
        this.showError('Failed to delete link. Please try again.');
      }
    },

    async handleAddNewHourLink() {
      if (!this.$refs.additionalHourLinkField.validate()) {
        return;
      }

      const newPost = { additional_hours_url: this.additionalHourLink };
      try {
        const response = await apiClient.patch(
          `${this.apiUrl}/v1/departments/${this.department.id}/`,
          newPost
        );

        if (response.data) {
          this.$set(
            this.department,
            "additional_hours_url",
            this.additionalHourLink
          );
          this.$router.replace({
            path: `/office_hour_manager/${this.department.id}`,
            query: {
              department: encodeURIComponent(
                JSON.stringify({
                  ...this.department,
                  additional_hours_url: this.additionalHourLink,
                })
              ),
            },
          });
        }
      } catch (error) {
        window.console.error("Error adding the additional hour:", error);
        this.showError('Failed to add link. Please try again.');
        return;
      }
      this.showNewAdditionalHour = false;
      this.resetAdditionalHour();
    },
    formatClosureDate(dateString) {
      const [year, month, day] = dateString.split("-");
      return `${month}-${day}-${year}`;
    },
  },
};
</script>

<style scoped>
p {
  margin: 0;
}

h1 {
  color: #000 !important;
  font-family: Graphik !important;
  font-size: 53px !important;
  font-style: normal !important;
  font-weight: 900 !important;
  line-height: normal !important;
}

.nav-tag {
  margin: 18px 0;
  color: var(--grey-grey, #9e9e9e);
  /* v-text/body-2 */
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  /* 142.857% */
  letter-spacing: 0.25px;
}

.nav-tag span {
  color: #7a6855;
  cursor: pointer;
}

h2 {
  color: #000 !important;
  font-family: Graphik !important;
  font-size: 30px !important;
  font-style: normal !important;
  font-weight: 300 !important;
  line-height: normal !important;
  margin-top: 20px;
}

.description {
  color: #85817c !important;
  font-family: Graphik !important;
  font-size: 16px !important;
  font-style: normal !important;
  font-weight: 400 !important;
  line-height: 34px !important;
}

.description a {
  color: black !important;
  font-weight: 900 !important;
  text-decoration: underline;
}

.no-hour-prompt-container {
  border-radius: 8px;
  border: 1px solid #2196f3;
  background: #e3f2fd;
}

.no-hour-prompt {
  display: flex;
  padding: 16px;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
}

.save-successful-container {
  border-radius: 8px;
  border: 1px solid #43a047;
  background: #e8f5e9;
}

.no-hour-prompt p {
  font-family: Graphik;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
  margin: 0;
}

.no-hour-message1 {
  color: #263238;
}

.no-hour-message2 {
  color: #546e7a;
  font-weight: 400;
  padding: 5px 0;
  /* align-self: stretch; */
}

h3 {
  color: #000;
  font-family: Graphik;
  font-size: 30px;
  font-style: normal;
  font-weight: 700;
  line-height: 40px;
}

.add-hour-group-btn {
  display: flex;
  height: 36px;
  padding: 0px 16px;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.add-hour-group-btn p {
  margin: 0;
  text-align: center;
  font-family: Graphik;
  font-size: 14px !important;
  font-style: normal !important;
  font-weight: 500 !important;
  line-height: 36px !important;
  letter-spacing: 1.25px !important;
  text-transform: uppercase;
}

.my-custom-sheet {
  background-color: rgba(0, 0, 0, 0.03) !important;
  border-radius: 8px;
  width: 100% !important;
}

.form-title {
  padding-top: 30px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.form-title-text {
  color: var(--black-087, rgba(0, 0, 0, 0.87));
  font-family: Graphik;
  font-style: normal;
  font-weight: 600;
}

.hour-group-form {
  padding: 0 30px;
  margin-top: 30px;
}

.hour-title {
  color: #000;
  font-family: Graphik;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0.1px;
  margin: 0;
}

.hour-subtitle {
  color: var(--black-06, rgba(0, 0, 0, 0.6));
  font-family: Graphik;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  /* 157.143% */
  letter-spacing: 0.1px;
  margin: 0;
}

.hour-label-container {
  margin-top: 20px;
}

.hour-label-container>>>.v-input__slot {
  background-color: white;
  border-radius: 8px;
}

.custom-label {
  color: #85817c !important;
  font-family: Graphik;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  /* background-color: white; */
}

.hour-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 30px 0;
}

.custom-switch {
  margin: 0;
}

.custom-switch>>>.v-input--switch__thumb {
  color: white !important;
}

.switch-on>>>.v-input--switch__track {
  color: #4caf50 !important;
  opacity: 1 !important;
}

.switch-off>>>.v-input--switch__track {
  color: #7a6855 !important;
  opacity: 1 !important;
}

.hour-data-row {
  display: flex;
  align-items: center;
  /* margin-bottom: 30px; */
  padding: 0;
  min-height: 50px;
  flex-wrap: wrap;
}

.hour-data-col {
  display: flex;
  align-items: center;
  padding: 0;
  margin-bottom: 25px;
  height: 53px;
  /* background-color: pink */
}

.weekday-text {
  color: var(--black-06, rgba(0, 0, 0, 0.6));
  font-family: Graphik;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  /* 150% */
  letter-spacing: 0.5px;
}

.time_container {
  position: relative;
}

.vue__time-picker {
  height: 53px;
  display: flex;
  align-items: center;
}

/* use v-deep to overrite node module styling! */
::v-deep .vue__time-picker input.display-time {
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding-left: 10px;
  height: 100%;
  width: 100%;
}

::v-deep .vue__time-picker .drop-down {
  margin-top: 15px;
}

::v-deep .vue__time-picker .controls {
  padding-right: 31px;
}

.time-icon {
  position: absolute;
  right: 10px;
  top: 15px;
  pointer-events: none;
}

.start_time_am_pm {
  height: 53px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.2);
  background-color: #fff;
  margin-left: 10px;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.start_time_am_pm p,
.all_day_coverage_text {
  margin: 0;
  padding: 0 14px;
  cursor: pointer;
  color: rgba(0, 0, 0, 0.87);
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%;
  /* 21px */
  letter-spacing: 0.21px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.start_time_am_pm p.active {
  background-color: rgba(197, 187, 177, 1);
}

.hour-data-col .v-text-field--outlined {
  max-height: 53px;
  max-width: 300px;
  border-radius: 8px;
}

::v-deep .hour-data-col .v-text-field--outlined .v-input__control .v-input__slot {
  min-height: 53px;
  background-color: #fff;
}

.fixed-column {
  flex: 0 0 180px;
}

.fixed-column1 {
  flex: 0 0 320px;
}

.override-dropdown {
  width: 280px;
}

::v-deep .override-dropdown .v-input__control .v-input__slot {
  background-color: #fff;
  border-radius: 8px;
}

::v-deep .v-input__control .v-label {
  color: #666 !important;
  font-family: Graphik !important;
  font-size: 16px;
  font-style: normal !important;
  font-weight: 400 !important;
  line-height: normal !important;
}

.form-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 40px 0 10px 0;
}

.dimmed-section {
  opacity: 0.5;
  pointer-events: none;
}

.closure-container {
  padding-top: 24px;
  width: 85%;
}

.error-message {
  position: absolute;
  top: 55px;
  color: red;
  font-size: 12px;
  margin-top: 5px;
  z-index: 2;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease-in-out, margin 1s ease-in-out;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  margin-bottom: 0;
}

.schedule-card-content {
  padding: 16px 30px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.schedule-card-description {
  color: rgba(0, 0, 0, 0.6);
  font-family: Graphik;
  font-size: 16px;
  font-style: italic;
  font-weight: 600;
  line-height: 32px;
  letter-spacing: 0.25px;
}

::v-deep .closure-container .v-text-field--outlined .v-input__control .v-input__slot {
  min-height: 53px;
  background-color: #fff;
}

.form-title-field {
  min-width: auto;
  max-width: 260px;
  width: 100%;
}
</style>
